import * as ngCore from '@angular/core'

import * as loader from '../../../../loader'

import * as components from './components'
import * as models from './models'
import { AtnDsPageHeaderStore } from './page-header.store'
/**
 * The Page Header component is a reusable UI element that is used to display various elements at the top of a page.
 * It utilizes projection slots, which allow for dynamic content to be inserted into specific areas of the component.
 * In the case of the Page Header component, it uses multiple projection slots to display different elements.
 * These slots act as insertion points for specific content.
 *
 * **Slots**
 * - `centered` The centered slot is a specialized content projection slot within the **atn-ds-page**
 * component that allows for the display of centered content in relation to the page header.
 * This slot is specifically designed to position content at the center of the page, aligned with
 * the header section. It provides a convenient way to showcase important information or visual
 * elements that require emphasis and attention.
 * - `actions` The right-aligned slot is a specialized content projection slot within the **atn-ds-page** component
 * that allows for the display of content aligned to the right of the page header. This slot is specifically designed
 * to position content at the right side of the page, in relation to the header section. It provides a convenient way
 * to showcase additional information or visual elements that complement the main content.
 * - `selectors` The selectors slot is utilized to present content that is positioned on the right side of the page
 * header, adjacent to the actions.
 *
 * When both the `actions` and `selectors` slots are included in the page header, they are presented side by side,
 * separated by a pipe symbol.
 *
 * **Inputs**
 * - {@link AtnDsPageHeaderComponent.name name}?
 * - {@link AtnDsPageHeaderComponent.breadcrumbs breadcrumbs}?
 *
 * @example
 * ```html
 * <!-- Page header with name and breadcrumbs -->
 * <atn-ds-page-header [name]="name" [breadcrumbs]="breadcrumbs"></atn-ds-page-header>
 *
 * <!-- Page header with name only -->
 * <atn-ds-page-header [name]="name"></atn-ds-page-header>
 *
 * <!-- Page header with centered content -->
 * <atn-ds-page-header>
 *  <div class="centered-content" centered>Page header content</div>
 * </atn-ds-page-header>
 *
 * <!-- Page header with centered content that is page header pagination children component of atn-ds-page-header -->
 * <atn-ds-page-header>
 *  <atn-ds-page-header-pagination centered></atn-ds-page-header-pagination>
 * </atn-ds-page-header>
 *
 * <!-- Page header with actions content -->
 * <atn-ds-page-header>
 *  <div class="actions-content" actions>Page actions content</div>
 * </atn-ds-page-header>
 *
 * <!-- Page header with selectors content -->
 * <atn-ds-page-header>
 *  <div class="selectors-content" selectors>Page selectors content</div>
 * </atn-ds-page-header>
 * ```
 */
@ngCore.Component({
  changeDetection: ngCore.ChangeDetectionStrategy.OnPush,
  imports: [components.Breadcrumbs.Component, loader.Component],
  providers: [AtnDsPageHeaderStore],
  selector: 'atn-ds-page-header',
  styleUrls: ['./page-header.component.scss'],
  templateUrl: './page-header.component.html',
})
export class AtnDsPageHeaderComponent {
  /** @see {@link AtnDsPageHeaderStore} */
  public readonly store = ngCore.inject(AtnDsPageHeaderStore)

  /**
   * The name of the page, displayed in the page header.
   */
  public readonly nameSg = ngCore.input<string | undefined>(undefined, {
    alias: 'name',
  })

  /**
   * The loading of the page header.
   *
   * @default false
   */
  public readonly loadingSg = ngCore.input<boolean | undefined>(false, {
    alias: 'loading',
  })

  /**
   * Error message to be displayed in the header in case of an error
   *
   * @default undefined
   */
  public errorSg = ngCore.input<string | undefined, string | Error | undefined>(
    undefined,
    {
      alias: 'error',
      transform: err => (err instanceof Error ? err.message : err),
    },
  )

  /**
   * The breadcrumbs for the page, will be displayed in the page header.
   */
  public readonly breadcrumbsSg = ngCore.input<models.Breadcrumb[] | undefined>(
    undefined,
    {
      alias: 'breadcrumbs',
    },
  )

  constructor() {
    ngCore.effect(() => {
      const name = this.nameSg()
      if (name !== undefined) {
        this.store.patchState({ name })
      }
    })

    ngCore.effect(() => {
      const breadcrumbs = this.breadcrumbsSg()
      if (breadcrumbs !== undefined) {
        this.store.patchState({ breadcrumbs })
      }
    })

    ngCore.effect(() => {
      const loading = this.loadingSg()
      if (loading !== undefined) {
        this.store.patchState({ loading })
      }
    })
  }
}
